import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom"; // Import useParams from react-router-dom
import Header from "../../components/HomePage/header";
import Footer from "../../components/HomePage/footer";
import Breadcrumbs from "./Breadcrum";
import ArticleHeader from "./ArticleHeader";
import Content from "./Content";
import BottomCtaModule from "./BottomCtaModule";
import PostTags from "./PostTags";
import AuthorBox from "./AutherBox";
import RelatedPosts from "./RelatedPosts";
import SidebarSubscribe from "./SidebarSubscribe";
import SidebarRelatedPosts from "./SidebarRelatedPosts";
import FloatingCtaModule from "./FloatingCtaModule";
import NewsletterPopup from "../../Account/NewsletterPopup";
import { Helmet } from "react-helmet";

const SITE_URL = "https://prepnexis.com";

const stripHtml = (value = "") =>
  String(value)
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (value = "", maxLength = 160) => {
  const text = String(value).trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3).trim()}...`;
};

const getPostSlug = (value = "") =>
  String(value)
    .replace(/^\/?blog\//, "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

const getPostImage = (post) => {
  if (!post?.featuredImage) return `${SITE_URL}/image/logoprep.png`;
  if (/^https?:\/\//i.test(post.featuredImage)) return post.featuredImage;
  const spacesUrl = process.env.REACT_APP_DO_SPACES_URL || "";
  if (spacesUrl) {
    return `${spacesUrl.replace(/\/+$/, "")}/${String(post.featuredImage).replace(/^\/+/, "")}`;
  }
  return `${SITE_URL}${String(post.featuredImage).startsWith("/") ? post.featuredImage : `/${post.featuredImage}`}`;
};

const getIsoDate = (...values) => {
  for (const value of values) {
    const date = value ? new Date(value) : null;
    if (date && !Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  }
  return new Date().toISOString();
};

export default function BlogPostPage() {
  const { link } = useParams(); // Use useParams to extract 'link' from the URL
  const [post, setPost] = useState(null);
  const [latestPosts, setLatestPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPosts = async (params, setState) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await fetch(`/api/posts?${queryParams}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch posts: ${response.statusText}`);
      }
      const data = await response.json();
      setState(data.posts);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    // Fetch individual post by link
    const fetchPost = async () => {
      try {
        const response = await fetch(`/api/posts/${link}`); // Use the 'link' from the URL
        if (response.ok) {
          const data = await response.json();
          setPost(data); // Set the fetched post data
        } else {
          throw new Error("Post not found");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();

    fetchPosts(
      {
        status: "published",
        sortBy: "createdAt",
        order: "desc",
        limit: 5,
        page: 1,
      },
      setLatestPosts
    );

    // Fetch featured posts
    fetchPosts(
      {
        status: "published",
        category: "Featured",
        sortBy: "createdAt",
        order: "desc",
        limit: 5,
        page: 1,
      },
      setFeaturedPosts
    );

    // Fetch news posts
    fetchPosts(
      {
        status: "published",
        category: "News",
        sortBy: "createdAt",
        order: "desc",
        limit: 5,
        page: 1,
      },
      setNewsPosts
    );
  }, [link]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-slate-50 px-4 py-12">
          <div className="mx-auto max-w-7xl rounded-md border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-semibold uppercase text-[rgb(126,34,206)]">
              Loading blog post
            </p>
            <div className="mt-4 h-8 max-w-xl rounded-md bg-slate-200" />
            <div className="mt-3 h-4 max-w-2xl rounded-md bg-slate-100" />
          </div>
        </main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-slate-50 px-4 py-12">
          <div className="mx-auto max-w-7xl rounded-md border border-red-100 bg-white p-6 shadow-sm">
            <p className="text-sm font-semibold uppercase text-red-500">
              Blog post unavailable
            </p>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">
              We could not load this article.
            </h1>
            <p className="mt-2 text-gray-600">{error}</p>
          </div>
        </main>
      </>
    );
  }

  const postSlug = getPostSlug(post.link || link);
  const canonicalUrl = `${SITE_URL}/blog/${postSlug}`;
  const metaTitle = `${post.title} | PrepNexis Blog`;
  const metaDescription = truncate(post.description || post.excerpt || stripHtml(post.content));
  const metaKeywords = Array.isArray(post.keywords)
    ? post.keywords.filter(Boolean).join(", ")
    : post.keywords;
  const imageUrl = getPostImage(post);
  const publishedAt = getIsoDate(post.createdAt, post.date);
  const modifiedAt = getIsoDate(post.updatedAt, post.createdAt, post.date);
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: metaDescription,
    image: imageUrl,
    author: {
      "@type": "Person",
      name: post.author || "PrepNexis",
    },
    publisher: {
      "@type": "Organization",
      name: "PrepNexis",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/image/logoprep.png`,
      },
    },
    mainEntityOfPage: canonicalUrl,
    datePublished: publishedAt,
    dateModified: modifiedAt,
  };

  return (
    <>
        <Helmet>
        <title>{metaTitle}</title>
        <meta
          name="description"
          content={metaDescription}
        />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="PrepNexis" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="article:published_time" content={publishedAt} />
        <meta property="article:modified_time" content={modifiedAt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Header />
      <main className="bg-slate-50">
        <Breadcrumbs post={post}/>
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[minmax(0,1fr),340px] lg:px-8">
          <article className="min-w-0">
            <ArticleHeader post={post} />
            <Content post={post} />
            <BottomCtaModule />
            <PostTags post={post} />
            <AuthorBox post={post} />
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <SidebarSubscribe />
              <SidebarRelatedPosts
                posts={latestPosts}
                featuredPosts={featuredPosts}
              />
              <FloatingCtaModule />
            </div>
          </aside>
        </div>
        <RelatedPosts posts={latestPosts} authorPosts={featuredPosts} />
      </main>
      <NewsletterPopup />
      <Footer />
    </>
  );
}
